// Report Groups Configuration
// This mirrors the Metabase collection structure: https://conexio.metabaseapp.com/collection/25-conexio-dashboards

export interface ReportParameter {
  slug: string;          // Parameter slug (e.g., "date_to_process")
  label: string;         // Display label for UI
  type: string;          // Metabase parameter type (e.g., 'date/single', 'string/=')
  defaultValue?: string | string[] | null; // Default value
  multiSelect?: boolean;
  required?: boolean;
}

export interface ReportDetails {
  name: string;
  dashboard_id: number;
  embed_url?: string; // Optional: fallback public URL
  parameters?: ReportParameter[]; // Optional: configurable parameters
}

export interface ReportSubcategory {
  name: string;
  reports: { [key: string]: ReportDetails };
}

export interface ReportGroup {
  id: string;
  category: string;
  description: string;
  reports?: { [key: string]: ReportDetails }; // Direct reports (flat structure)
  subcategories?: { [key: string]: ReportSubcategory }; // Nested reports (tree structure)
}

// Integration Mode
export const USE_BACKEND_API = process.env.REACT_APP_USE_BACKEND_API === 'true';

// API Configuration (for backend integration)
export const apiConfig = {
  baseUrl: process.env.REACT_APP_API_BASE_URL || 'https://coco.fleetwatch.ai',
  reportGroupsEndpoint: '/report-groups',
  authToken: process.env.REACT_APP_API_AUTH_TOKEN || '',
};

// Static Report Groups - Mirrors Metabase Collection Structure
// Collection 25: Conexio Dashboards > Applications (Collection 30)
export const staticReportGroups: ReportGroup[] = [
  // ================================================================
  // BTR (Battery Telemetry Report)
  // ================================================================
  {
    id: 'btr',
    category: 'BTR',
    description: 'Battery Telemetry Report - Geographic distribution and metrics',
    reports: {
      'geographic_breakdown': {
        name: 'Geographic Breakdown of EV Vehicles',
        dashboard_id: 57,
        embed_url: 'https://conexio.metabaseapp.com/public/dashboard/a8fe64db-f472-4aaa-bb48-9e08102bbb7e',
      },
      'btr_report_metrics': {
        name: 'BTR Report Metrics (Table)',
        dashboard_id: 65,
        parameters: [
          {
            slug: 'date_to_process',
            label: 'Date To Process',
            type: 'date/single',
            defaultValue: new Date().toISOString().split('T')[0], // Today's date
          },
        ],
      },
      'btr_data_flow_visualization': {
        name: 'BTR Data Flow Visualization',
        dashboard_id: 60,
        embed_url: 'https://conexio.metabaseapp.com/public/dashboard/b57b0c60-d042-4772-a1ae-57364de361c0',
      },
      'btr_vehicle_breakdown': {
        name: 'BTR Vehicle Breakdown',
        dashboard_id: 0, // TODO: Get actual dashboard ID
        embed_url: 'https://conexio.metabaseapp.com/public/dashboard/ba7eff29-1436-45c7-97fb-d9039d04cf4d?processed_at=',
      },
      'btr_exceptions': {
        name: 'BTR Exceptions',
        dashboard_id: 0, // TODO: Get actual dashboard ID
        embed_url:
          'https://conexio.metabaseapp.com/public/dashboard/72176fb5-7ca4-4b6a-8e10-433adb09c2e0?recharge_event_date=2025-10-11~2025-11-10',
      },
      // TODO: Add more BTR dashboards - enable public sharing in Metabase first
    },
  },

  // ================================================================
  // DLM (Dealer Lot Management)
  // ================================================================
  {
    id: 'dlm',
    category: 'DLM',
    description: 'Dealer Lot Management reports',
    subcategories: {
      'dlm_reporting': {
        name: 'DLM Reporting',
        reports: {
          'dlm_overview': {
            name: 'Vehicles Gold Table (Fleetwatch/Prod/vehicles)',
            dashboard_id: 0, // TODO: Get actual dashboard ID
            embed_url: 'https://conexio.metabaseapp.com/public/dashboard/afaaf4bb-82be-4c99-9f8d-2bc4e1a45055',
          }, 'deler_poc': {
            name: 'Dealer PoC',
            dashboard_id: 0, // TODO: Get actual dashboard ID
            embed_url: 'https://conexio.metabaseapp.com/public/dashboard/84407fcd-860d-4e37-8b82-35e97f70af2b',
          }
        },
      },
      'inventory_lifecycle': {
        name: 'Inventory & Lifecycle Management Reports',
        reports: {
          'inventory_lifecycle_1': {
            name: 'Aged Inventory & Lot Duration Report',
            dashboard_id: 0, // TODO: Get actual dashboard ID
            embed_url: 'https://conexio.metabaseapp.com/public/dashboard/980d5c91-e794-4840-be17-43b1ea79f6d0',
          },
          'incoming_vehicle_pdi': {
            name: 'Incoming Vehicle & PDI Readiness Report',
            dashboard_id: 24,
            embed_url: 'https://conexio.metabaseapp.com/public/dashboard/c21c1991-1c07-48c0-90b4-a7b945bc9a09',
          },
          'inventory_lifecycle_3': {
            name: 'Sales Conversion & Enrollment Report',
            dashboard_id: 0, // TODO: Get actual dashboard ID
            embed_url: 'https://conexio.metabaseapp.com/public/dashboard/36c487df-1afc-446b-8e82-e2b07568a559',
          },
        },
      },
      'security_compliance': {
        name: 'Security & Compliance Reports',
        reports: {
          'security_compliance_1': {
            name: 'Breach Summary & Trends',
            dashboard_id: 0, // TODO: Get actual dashboard ID
            embed_url: 'https://conexio.metabaseapp.com/public/dashboard/ebc1b588-c03d-45f5-897e-e940bf8d7f25',
          },
          'security_breach_log': {
            name: 'Security Breach Log',
            dashboard_id: 31,
            embed_url: 'https://conexio.metabaseapp.com/public/dashboard/9caba71e-f7b5-403d-bc84-e47e915a4072',
          },
        },
      },
      'special_vehicle_program': {
        name: 'Special Vehicle Program (Loaner/Demo) Reports',
        reports: {
          'special_vehicle_1': {
            name: 'Loaner Vehicle Program Report',
            dashboard_id: 0, // TODO: Get actual dashboard ID
            embed_url: 'https://conexio.metabaseapp.com/public/dashboard/d8cfb78c-11e3-4435-87a9-798621b671d3',
          },
          'special_vehicle_2': {
            name: 'Sales Demo Vehicle Performance Report',
            dashboard_id: 0, // TODO: Get actual dashboard ID
            embed_url: 'https://conexio.metabaseapp.com/public/dashboard/2bd0b264-cfe2-4521-b80e-f54646dc5cf3',
          },
        },
      },
      'vehicle_health_upkeep': {
        name: 'Vehicle Health & Upkeep Reports',
        reports: {
          'vehicle_health_1': {
            name: 'Battery Health Trend Analysis ',
            dashboard_id: 0, // TODO: Get actual dashboard ID
            embed_url: 'https://conexio.metabaseapp.com/public/dashboard/34cffc7f-44b1-4149-94b8-0f37dd2ba51b',
          },
          'vehicle_health_2': {
            name: 'High-Risk Vehicle Health Dashboard',
            dashboard_id: 0, // TODO: Get actual dashboard ID
            embed_url: 'https://conexio.metabaseapp.com/public/dashboard/ba066f2a-83dc-4243-a71c-9b92c84772e4',
          },
          'vehicle_health_3': {
            name: 'Overdue Upkeep & Action Report',
            dashboard_id: 0, // TODO: Get actual dashboard ID
            embed_url: 'https://conexio.metabaseapp.com/public/dashboard/c894da6e-f5c8-4bc4-b7f0-5cdc542fef16',
          },
          'vehicle_health_4': {
            name: 'Tire Health & Movement Effectiveness Report',
            dashboard_id: 0, // TODO: Get actual dashboard ID
            embed_url: 'https://conexio.metabaseapp.com/public/dashboard/f7f6b70c-885a-47e3-859c-0ea854f02a43',
          },
        },
      },
    },
  },

  // ================================================================
  // Fleet
  // ================================================================
  {
    id: 'fleet',
    category: 'Fleet',
    description: 'Fleet management and analytics',
    reports: {
      // TODO: Browse Fleet folder, add dashboards with public URLs
    },
  },

  // ================================================================
  // MZK (Monozukuri)
  // ================================================================
  {
    id: 'mzk',
    category: 'MZK',
    description: 'Monozukuri manufacturing and logistics',
    reports: {
      // TODO: Browse MZK folder, add dashboards with public URLs
    },
  },

  // ================================================================
  // Agents
  // ================================================================
  {
    id: 'agents',
    category: 'Agents',
    description: 'Agent performance and analytics',
    reports: {
      // TODO: Browse Agents folder, add dashboards with public URLs
    },
  },

  // ================================================================
  // Applets
  // ================================================================
  {
    id: 'applets',
    category: 'Applets',
    description: 'Application insights and metrics',
    reports: {
      // TODO: Browse Applets folder, add dashboards with public URLs
    },
  },

  // ================================================================
  // Coco
  // ================================================================
  {
    id: 'coco',
    category: 'Coco',
    description: 'Coco AI assistant analytics',
    reports: {
      // TODO: Browse Coco folder, add dashboards with public URLs
    },
  },

  // ================================================================
  // Dealers
  // ================================================================
  {
    id: 'dealers',
    category: 'Dealers',
    description: 'Dealer performance and analytics',
    reports: {
      // TODO: Browse Dealers folder, add dashboards with public URLs
    },
  },

  // ================================================================
  // Ingestion
  // ================================================================
  {
    id: 'ingestion',
    category: 'Ingestion',
    description: 'Data ingestion monitoring and metrics',
    reports: {
      'ingestion_dashboard_1': {
        name: 'Vehicle Ingestion Data',
        dashboard_id: 0, // TODO: Get actual dashboard ID from Metabase
        embed_url: 'https://conexio.metabaseapp.com/public/dashboard/a4882564-2bac-45b5-84bb-faacd5bd2a5d',
      },
      'ingestion_dashboard_2': {
        name: 'Vehicle Observability',
        dashboard_id: 0, // TODO: Get actual dashboard ID from Metabase
        embed_url: 'https://conexio.metabaseapp.com/public/dashboard/611ce5e0-5b23-4b41-a2a1-5c0d3698210f',
      },
      'ingestion_dashboard_3': {
        name: 'Vehicle Snowflake data',
        dashboard_id: 0, // TODO: Get actual dashboard ID from Metabase
        embed_url: 'https://conexio.metabaseapp.com/public/dashboard/1803a39b-5fe2-4ee0-a13c-1e3bb9226586',
      },
      'ingestion_dashboard_4': {
        name: 'Vehicle VDMS Data',
        dashboard_id: 0, // TODO: Get actual dashboard ID from Metabase
        embed_url: 'https://conexio.metabaseapp.com/public/dashboard/ce46b524-528f-4b00-b408-4dc4c479af91',
      },
    },
  },

  // ================================================================
  // Vehicles
  // ================================================================
  {
    id: 'vehicles',
    category: 'Vehicles',
    description: 'Vehicle tracking and analytics',
    reports: {
      // TODO: Browse Vehicles folder, add dashboards with public URLs
    },
  },
];
